<template>
  <!-- Temporarily removing teleport for testing -->
  <div
    v-if="isVisible"
    class="fixed inset-0 z-50 flex items-center justify-center p-4"
    style="background-color: rgba(0, 0, 0, 0.9)"
    @click="handleOverlayClick"
  >
      <div
        class="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl border border-neutral-700"
        :style="backgroundStyle"
        @click.stop
      >
      <!-- Close button -->
      <button
        @click="handleClose"
        class="absolute top-6 right-6 z-10 p-1 text-gray-400 hover:text-white transition-colors"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <!-- Main content -->
      <div class="px-8 py-12 text-center">
        <!-- Star icon with enhanced animation and glow -->
        <div class="mb-8 relative">
          <div class="star-animation">
            <img
              src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png"
              alt="Star"
              class="w-16 h-16 mx-auto"
            />
          </div>
          <!-- Enhanced pulsing glow -->
          <div
            class="absolute inset-0 w-16 h-16 mx-auto rounded-full blur-xl glow-animation"
            :style="glowStyle"
          />
        </div>

        <!-- Content based on user type -->
        <div v-if="isVisitor" class="space-y-6">
          <div class="space-y-3">
            <h2 class="text-xl text-white font-medium">Unlock Star Power</h2>
            <p class="text-blue-400 text-sm font-medium mb-3 flex items-center justify-center gap-1">
              Click the Star Icon
              <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="Star" class="w-4 h-4 inline" />
              to Show Support
            </p>
            <p class="text-neutral-400 text-sm leading-relaxed">
              Your sponsorship turns auditions into premium productions. Support your favorite performers and help decide what content gets made.
            </p>
          </div>

          <a
            :href="route('www.join.signUp', {custom8: 'NA_Auditions_Modal'})"
            class="w-full py-4 rounded-2xl text-base font-medium transition-all duration-200 border-0 relative overflow-hidden bg-[#0052ce] hover:bg-[#003d99] shadow-lg cursor-pointer block"
            style="box-shadow: 0 8px 32px rgba(220, 38, 38, 0.3);"
            @click="handleJoinClick"
          >
            <span class="relative z-10 text-white">Join to start sponsoring</span>
          </a>

          <div class="mt-4 space-y-3">
            <a
              href="https://www.naughtyamerica.com/auditions"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs text-neutral-400 hover:text-neutral-200 transition-colors underline block"
            >
              How it works
            </a>

            <button
              @click="handleDontShowAgain"
              class="text-xs text-neutral-500 hover:text-neutral-300 transition-colors"
            >
              Got it, don't show me this again
            </button>
          </div>
        </div>

        <!-- Member content -->
        <div v-else class="space-y-6">
          <div class="space-y-3">
            <h2 class="text-xl text-white font-medium">Sponsor This Audition</h2>
            <p class="text-blue-400 text-sm font-medium mb-3 flex items-center justify-center gap-1">
              Click the Star Icon
              <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="Star" class="w-4 h-4 inline" />
              to Show Support
            </p>
          </div>

          <!-- Bullet points -->
          <div class="space-y-3 py-4">
            <div class="flex items-center gap-3 text-left">
              <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="Star" class="w-4 h-4 flex-shrink-0 opacity-80" />
              <span class="text-gray-300 text-sm">Your stars help decide what we film next</span>
            </div>
            <div class="flex items-center gap-3 text-left">
              <svg class="w-4 h-4 flex-shrink-0 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <span class="text-gray-300 text-sm">Watch the goal grow as fans contribute</span>
            </div>
            <div class="flex items-center gap-3 text-left">
              <svg class="w-4 h-4 flex-shrink-0 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
              <span class="text-gray-300 text-sm">As a subscriber, starring is free. Star away!</span>
            </div>
            <div class="flex items-center gap-3 text-left">
              <svg class="w-4 h-4 flex-shrink-0 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span class="text-gray-300 text-sm">Enjoy free stars each month to support new auditions.</span>
            </div>
          </div>

          <div class="py-2">
            <p class="text-blue-400 text-sm font-medium">Don't just watch it. Get the thrill of knowing you helped make it happen.</p>
          </div>

          <div
            class="w-full py-4 rounded-2xl text-base font-medium transition-all duration-200 border-0 relative overflow-hidden bg-blue-600 hover:bg-blue-700 shadow-lg cursor-pointer"
            style="box-shadow: 0 8px 32px rgba(37, 99, 235, 0.3);"
            @click="handleStartStarring"
          >
            <span class="relative z-10 text-white">Start Sponsoring</span>
          </div>

          <div class="mt-4 space-y-3">
            <a
              href="https://members.naughtyamerica.com/auditions"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs text-gray-500 hover:text-gray-300 transition-colors underline block"
            >
              Learn more about auditions
            </a>

            <button
              @click="handleDontShowAgain"
              class="text-xs text-neutral-500 hover:text-neutral-300 transition-colors"
            >
              Got it, don't show me this again
            </button>
          </div>
        </div>

        <!-- Logo at bottom -->
        <div class="mt-8 pt-4 border-t border-white/10">
          <img src="/images/fyf/nalogo-fyf.png" alt="Naughty America" class="h-5 mx-auto opacity-60" />
        </div>
      </div>
      </div>
    </div>
  <!-- </Teleport> -->
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  userType: {
    type: String,
    default: 'visitor', // 'visitor' or 'member'
    validator: (value) => ['visitor', 'member'].includes(value)
  }
})

const emit = defineEmits(['close', 'dont-show-again', 'join-click', 'start-starring'])

const isVisitor = computed(() => props.userType === 'visitor')

const backgroundStyle = computed(() => {
  return isVisitor.value
    ? 'background: linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(20, 0, 0, 0.95) 50%, rgba(0, 0, 0, 0.95) 100%)'
    : 'background: linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(0, 10, 20, 0.95) 50%, rgba(0, 0, 0, 0.95) 100%)'
})

const glowStyle = computed(() => {
  return isVisitor.value
    ? 'background: radial-gradient(circle, #dc2626 0%, transparent 70%)'
    : 'background: radial-gradient(circle, #2563eb 0%, transparent 70%)'
})

const handleOverlayClick = () => {
  emit('close')
}

const handleClose = () => {
  emit('close')
}

const handleDontShowAgain = () => {
  emit('dont-show-again')
  emit('close')
}

const handleJoinClick = () => {
  emit('join-click')
  // Track with custom parameter
  if (typeof fathom !== 'undefined') {
    fathom.trackEvent('NA Auditions Modal', {
      action: 'join_click',
      user_type: props.userType
    })
  }
  // Redirect to join page
  window.location.href = '/join'
}

const handleStartStarring = () => {
  emit('start-starring')
  emit('close')
}
</script>

<style scoped>
.star-animation {
  animation: starFloat 4s ease-in-out infinite;
}

@keyframes starFloat {
  0%, 100% {
    transform: scale(1) rotate(0deg) translateY(0);
  }
  25% {
    transform: scale(1.1) rotate(5deg) translateY(-4px);
  }
  50% {
    transform: scale(1.1) rotate(-5deg) translateY(-4px);
  }
  75% {
    transform: scale(1.1) rotate(5deg) translateY(-4px);
  }
}

.glow-animation {
  animation: glowPulse 2.5s ease-in-out infinite;
}

@keyframes glowPulse {
  0%, 100% {
    opacity: 0.2;
    transform: scale(0.8);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.3);
  }
}

/* Star button highlight animation */
.star-highlight {
  animation: starHighlight 2.5s ease-in-out;
}

@keyframes starHighlight {
  0% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(59, 130, 246, 0.3);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
  }
}
</style>
